import { Component, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { TranslationService } from '../../services/translation.service';

@Component({
    selector: 'app-cookie-consent-popup',
    templateUrl: './cookie-consent-popup.component.html',
    styleUrls: ['./cookie-consent-popup.component.scss'],
    standalone: false
})
export class CookieConsentPopupComponent implements OnInit, OnDestroy {
  @Output() accepted = new EventEmitter<void>();

  consentGiven: boolean = false;
  showModal: boolean = false;
  currentLang: string = 'en';
  selectedCategory: string | null = null;
  //private langChangeSub!: Subscription;
  private destroy$ = new Subject<void>();

  preferences = {
    functional: false,
    analytics: false,
    performance: false,
    advertisement: false,
    uncategorized: false
  };

  translations = {
    en: {
      message: 'By clicking "Accept All", you agree to our use of cookies...',
      privacyPolicy: 'SEEMYPASS Privacy Policy',
      reject: 'Reject All',
      customize: 'Customize',
      accept: 'Accept All',
      save: 'My Preferences',
      essential: 'Essential',
      functional: 'Functional',
      analytics: 'Analytics',
      performance: 'Performance',
      advertisement: 'Advertisement',
      uncategorized: 'Uncategorized',
      essentialDescription: 'Essential cookies are crucial for the core functions of this website—such as secure login and saving your consent settings. These cookies do not collect any information that can personally identify you.',
      functionalDescription: 'Functional cookies enable specific features on the website, such as sharing content on social media, collecting user feedback, and supporting other third-party services.',
      analyticsDescription: 'Analytics cookies help us understand how visitors engage with the website. They provide insights into metrics like visitor numbers, bounce rates, traffic sources, and more, allowing us to improve site performance and user experience.',
      performanceDescription: 'Performance cookies help track and analyze key website metrics, enabling us to enhance the user experience for visitors.',
      advertisementDescription: 'Advertising cookies deliver personalized ads tailored to your browsing history and help measure how well our advertising campaigns perform.',
      uncategorizedDescription: 'Uncategorized cookies are those that are still under review and have not yet been assigned to a specific category.',
      newMessage: `Manage Cookie Preferences
      To ensure this site works properly and to enhance your experience, we use cookies.
      You can review and manage your cookie preferences by category below.
      Cookies marked as “Necessary” are essential for the basic functions of the site and are stored in your browser automatically.
      We also use cookies from third parties to understand how you use our site, remember your preferences, and show you relevant content and ads.
      These cookies will only be used with your consent.`,
      shortMessage: `We use cookies to ensure this site works properly and to improve your experience.
      You can manage your preferences by category below.
      Essential cookies are always active; others require your consent.`
    },
    fr: {
      message: 'En cliquant sur "Tout Accepter", vous acceptez l\'utilisation de cookies...',
      privacyPolicy: 'Politique de confidentialité SEEMYPASS',
      reject: 'Tout Rejeter',
      customize: 'Personnaliser',
      accept: 'Tout Accepter',
      save: 'Mes Préférences',
      essential: 'Nécessaires',
      functional: 'Fonctionnels',
      analytics: 'Analytiques',
      performance: 'Performance',
      advertisement: 'Publicité',
      uncategorized: 'Non catégorisé',
      essentialDescription: 'Les cookies essentiels sont indispensables au bon fonctionnement de ce site, notamment pour la connexion sécurisée et l’enregistrement de vos préférences de consentement. Ces cookies ne collectent aucune information permettant de vous identifier personnellement.',
      functionalDescription: 'Les cookies fonctionnels permettent d’activer certaines fonctionnalités du site, comme le partage de contenu sur les réseaux sociaux, la collecte de retours d’utilisateurs et le support d’autres services tiers.',
      analyticsDescription: 'Les cookies analytiques nous aident à comprendre comment les visiteurs interagissent avec le site web. Ils fournissent des informations sur des indicateurs tels que le nombre de visiteurs, le taux de rebond, les sources de trafic, et bien plus encore, afin d\'améliorer les performances du site et l’expérience utilisateur.',
      performanceDescription: 'Les cookies de performance permettent de suivre et d’analyser les principaux indicateurs du site afin d’améliorer l’expérience utilisateur des visiteurs.',
      advertisementDescription: 'Les cookies publicitaires permettent de vous présenter des annonces personnalisées en fonction des pages que vous avez consultées auparavant et d’évaluer l’efficacité de nos campagnes publicitaires.',
      uncategorizedDescription: 'Les cookies non classés sont ceux qui sont encore en cours d’analyse et qui n’ont pas encore été attribués à une catégorie spécifique.',
      newMessage: `Gérer les préférences de cookies
      Pour que ce site fonctionne correctement et pour améliorer votre expérience, nous utilisons des cookies.
      Vous pouvez consulter et gérer vos préférences de cookies par catégorie ci-dessous.
      Les cookies marqués comme « Nécessaires » sont essentiels aux fonctions de base du site et sont automatiquement stockés dans votre navigateur.
      Nous utilisons également des cookies tiers pour comprendre comment vous utilisez notre site, mémoriser vos préférences, et vous proposer du contenu et des publicités pertinents.
      Ces cookies ne seront utilisés qu’avec votre consentement.`,
      shortMessage: `Nous utilisons des cookies pour assurer le bon fonctionnement du site et améliorer votre expérience.
    Vous pouvez gérer vos préférences par catégorie ci-dessous.
    Les cookies essentiels sont toujours actifs ; les autres nécessitent votre consentement.`
    }
  };

  constructor(
    //private translate: TranslateService
    private translationService: TranslationService
  ) {}

  ngOnInit() {
    /*
    this.currentLang = this.translate.currentLang || 'en';

    this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.currentLang = event.lang;
    });
    */
    this.currentLang = this.translationService.getLanguage() || 'en';

    this.translationService.getLanguageObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.currentLang = event;
      });
  }

  ngOnDestroy() {
    /*
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    */
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRejectAll() {
    this.preferences = {
      functional: false,
      analytics: false,
      performance: false,
      advertisement: false,
      uncategorized: false
    };
    this.saveConsent();
  }

  onAcceptAll() {
    this.preferences = {
      functional: true,
      analytics: true,
      performance: true,
      advertisement: true,
      uncategorized: true
    };
    this.saveConsent();
  }

  onCustomize() {
    this.showModal = true;
  }

  onSave() {
    this.saveConsent();
  }

  saveConsent() {
    localStorage.setItem('cookiePreferences', JSON.stringify(this.preferences));
    localStorage.setItem('cookieConsent', 'true');
    this.consentGiven = true;
    this.accepted.emit();
    this.showModal = false;
  }

  toggleDetails(category: string) {
    this.selectedCategory = this.selectedCategory === category ? null : category;
  }
}
